/** Fallback snap threshold (px) when fewer than two boundaries are available. */
export const DEFAULT_SNAP_THRESHOLD = 20;

/**
 * Snaps `rawHeight` to the nearest row boundary within `snapThreshold` pixels.
 * Returns `rawHeight` unchanged when no boundary is close enough.
 *
 * - Threshold defaults to half the minimum gap between boundaries so the snap
 *   zone covers the nearest half of each row regardless of row height.
 * - When `rawHeight` is beyond the last known boundary, the grid is extrapolated
 *   forward using the minimum gap to handle rows not yet reported by the child.
 */
export const snapHeightToRowBoundary = (
	rawHeight: number,
	boundaries: number[] | undefined,
	options?: {
		minHeight?: number;
		snapThreshold?: number;
	},
): number => {
	const minHeight = options?.minHeight;

	if (!boundaries || boundaries.length === 0) {
		return rawHeight;
	}

	const eligible = boundaries.filter(
		(b) => Number.isFinite(b) && (minHeight === undefined || b >= minHeight),
	);

	if (eligible.length === 0) {
		return rawHeight;
	}

	const sorted = [...eligible].sort((a, b) => a - b);

	let minGap = Infinity;
	for (let i = 1; i < sorted.length; i++) {
		const gap = sorted[i] - sorted[i - 1];
		if (gap > 0 && gap < minGap) {
			minGap = gap;
		}
	}

	const snapThreshold: number =
		options?.snapThreshold !== undefined
			? options.snapThreshold
			: minGap === Infinity
				? DEFAULT_SNAP_THRESHOLD
				: Math.floor(minGap / 2);

	let bestHeight: number | null = null;
	let bestDistance = snapThreshold;
	for (const boundary of eligible) {
		const distance = Math.abs(boundary - rawHeight);
		if (distance <= bestDistance) {
			bestDistance = distance;
			bestHeight = boundary;
		}
	}
	if (bestHeight !== null) {
		return bestHeight;
	}

	if (minGap !== Infinity && rawHeight > sorted[sorted.length - 1]) {
		const lastBoundary = sorted[sorted.length - 1];
		const projected = lastBoundary + Math.round((rawHeight - lastBoundary) / minGap) * minGap;
		if (Math.abs(rawHeight - projected) <= snapThreshold) {
			return projected;
		}
	}

	return rawHeight;
};
